import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { View } from '../../types';
import VetDashboard from './VetDashboard';
import { Capacitor } from '@capacitor/core';
import { LoginView, RegisterView } from '../../components/AuthViews';
import { VetHome } from './VetHome';
import { 
    Stethoscope, ArrowLeft, Heart,
    Zap, CheckCircle2, ArrowRight, Package, MapPin,
    Activity, ShieldCheck, Mail, LogOut,
    Users, Calendar, ChevronRight
} from 'lucide-react';

interface VetPortalViewProps {
    onNavigate: (view: View) => void;
}

export const VetPortalView: React.FC<VetPortalViewProps> = ({ onNavigate }) => {
    const { user, logout } = useAuth();
    // En la web, quien entra a /veterinarias sin sesión ve primero la portada
    // (como ya pasa en /criaderos); en la APK nativa de veterinaria se va
    // directo al ingreso. Antes esto siempre caía en 'login' en la web,
    // sin mostrar nunca la portada — mismo bug que tenía /albergues.
    const [activePortalTab, setActivePortalTab] = useState<'discover' | 'login' | 'register'>(Capacitor.isNativePlatform() ? 'login' : 'discover');

    // Sync browser tab or query parameters if they are set (for seamless direct link support)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        if (action === 'login') {
            setActivePortalTab('login');
        } else if (action === 'register' || params.get('role') === 'vet') {
            setActivePortalTab('register');
        }
    }, []);

    // Custom inner navigation interceptor to keep vet auth experience inside /veterinaria
    const handleInnerNavigate = (view: View) => {
        console.log("VetPortalView Intercepted navigation:", view);
        if (view === View.REGISTER) {
            setActivePortalTab('register');
        } else if (view === View.LOGIN) {
            setActivePortalTab('login');
        } else if (view === View.LANDING || view === View.VET_LANDING) {
            setActivePortalTab('discover');
        } else if (view === View.VET_DASHBOARD) {
            // Already logged in, the auth state change will automatically trigger rendering the Dashboard
            console.log("Logged in successfully, waiting for auth context update");
        } else {
            onNavigate(view);
        }
    };

    const handleLogout = () => {
        if (confirm("¿Estás seguro de que quieres cerrar sesión del Portal Pro?")) {
            logout();
            setActivePortalTab('login');
        }
    };

    // Veterinario por rol propio o por solicitud de socio aprobada (acceso multi-portal,
    // misma cuenta que su panel de usuario normal — ver BreederPortalView.tsx).
    const isVet = !!user && (user.role?.includes('vet') || (user as any).partner_roles?.includes('vet') || user.role?.includes('root'));

    // CASE 1: Veterinarian is Logged In
    if (user && isVet) {
        return (
            <div className="min-h-screen bg-slate-50 w-full animate-in fade-in duration-500">
                <VetDashboard onLogout={handleLogout} />
            </div>
        );
    }

    // CASE 2 eliminado: un usuario logueado SIN el rol de veterinaria ya no ve una
    // pantalla de "acceso restringido" — cae directo al flujo normal de invitado
    // (CASE 3), donde puede registrarse como veterinaria (eso crea una solicitud de
    // socio para la MISMA cuenta, no una cuenta nueva) o iniciar sesión con otra cuenta.

    // CASE 3: Guest / Not Logged In (Landing + Login + Register Hub)
    return (
        <div className="bg-white min-h-screen overflow-x-hidden font-sans selection:bg-teal-500 selection:text-white">
            {/* Top Branded Navigation Header */}
            <nav className="fixed top-0 left-0 w-full bg-white/70 backdrop-blur-xl z-50 border-b border-teal-100/60 px-6 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button 
                        onClick={() => {
                            setActivePortalTab('discover');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                        className="flex items-center gap-2.5 group"
                    >
                        <div className="bg-gradient-to-br from-[#0ea5a4] to-[#0284c7] p-2.5 rounded-2xl text-white group-hover:rotate-12 transition-transform shadow-lg shadow-teal-100">
                            <Stethoscope size={20} />
                        </div>
                        <div className="text-left">
                            <span className="font-bold text-[#0f2f3a] text-sm sm:text-base leading-none block whitespace-nowrap" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                                <span className="sm:hidden">Veterinarias</span><span className="hidden sm:inline">Amigo Peludo Veterinarias</span>
                            </span>
                            <span className="hidden sm:block text-[8px] font-bold text-teal-600 tracking-[0.25em] uppercase mt-0.5">Consulta · Cuidado · Familia</span>
                        </div>
                    </button>

                    {/* Navigation Actions — visible en todos los tamaños */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button
                            onClick={() => setActivePortalTab('discover')}
                            className={`hidden sm:flex px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'discover'
                                    ? 'bg-teal-50 text-teal-700'
                                    : 'text-slate-500 hover:text-slate-800'
                            }`}
                        >
                            Descubrir
                        </button>
                        <button
                            onClick={() => setActivePortalTab('login')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'login'
                                    ? 'bg-gradient-to-r from-[#0ea5a4] to-[#0284c7] text-white shadow-lg shadow-teal-200'
                                    : 'border border-teal-100 text-[#24505a] hover:bg-teal-50'
                            }`}
                        >
                            Ingresar
                        </button>
                        <button
                            onClick={() => setActivePortalTab('register')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'register'
                                    ? 'bg-gradient-to-r from-[#0ea5a4] to-[#0284c7] text-white'
                                    : 'bg-[#0f2f3a] text-white hover:bg-teal-600'
                            }`}
                        >
                            <span className="hidden sm:inline">Solicitar </span>Cuenta
                        </button>
                    </div>
                </div>
            </nav>

            {/* Portada (Descubrir): ocupa todo el ancho, con su propio hero bajo la barra fija */}
            {activePortalTab === 'discover' && (
                <VetHome
                    onRegister={() => { setActivePortalTab('register'); window.scrollTo({ top: 0 }); }}
                    onLogin={() => { setActivePortalTab('login'); window.scrollTo({ top: 0 }); }}
                />
            )}

            {/* Ingreso y registro */}
            <div className={`${activePortalTab === 'discover' ? 'hidden' : ''} pt-24 md:pt-28 pb-20 max-w-7xl mx-auto px-6 animate-in fade-in duration-300`}>
                {activePortalTab === 'login' && (
                    <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Injected Login View locked to VET with inner navigate interceptor */}
                        <LoginView 
                            initialMode="VET" 
                            hideRegister={true} 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}

                {activePortalTab === 'register' && (
                    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Injected Register View locked to Vet with inner navigate interceptor */}
                        <RegisterView 
                            role="vet" 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default VetPortalView;
